type Example = [eraql: string, expected: any];

type Doc = {
  description: string;
  examples: Example[];
  advanced?: boolean;
};

type Docs = Record<string, Doc>;

export const REL_SPECIAL_FORMS_DOCS: Docs = {
  Values: {
    description: 'Create a relation from literal values',
    examples: [
      [
        `Values([
          { id: 1, name: 'Alice' },
          { id: 2, name: 'Bob' }
        ])`,
        [
          { id: 1, name: 'Alice' },
          { id: 2, name: 'Bob' },
        ],
      ],
      [`Values([{ x: 1 }])`, [{ x: 1 }]],
    ],
  },

  Select: {
    description: 'Select and transform columns from a relation',
    examples: [
      [
        `Values([{ x: 1, y: 2 }])
        |> Select({ 
          sum: add("x", "y"),
          doubled_x: mul("x", 2)
        })`,
        [{ sum: 3, doubled_x: 2 }],
      ],
      [
        `Values([{ name: 'Alice', age: 30 }])
        |> Select(
          { name: "name", adult: gte("age", 18) },
          { distinct: true }
        )`,
        [{ name: 'Alice', adult: true }],
      ],
    ],
  },

  Where: {
    description: 'Filter rows in a relation based on a condition',
    examples: [
      [
        `Values([
          { age: 25, name: 'Alice' },
          { age: 17, name: 'Bob' }
        ])
        |> Where(gte("age", 18))`,
        [{ age: 25, name: 'Alice' }],
      ],
      [
        `Values([{ x: 1 }, { x: 2 }, { x: 3 }])
        |> Where(eq("x", 2))`,
        [{ x: 2 }],
      ],
    ],
  },

  Aggregate: {
    description: 'Group and aggregate data in a relation',
    examples: [
      [
        `Values([
          { dept: 'IT', salary: 50000 },
          { dept: 'IT', salary: 60000 },
          { dept: 'HR', salary: 45000 }
        ])
        |> Aggregate(
          ["dept"],
          { avg_salary: avg("salary"), dept: "dept" }
        )`,
        [
          { dept: 'IT', avg_salary: 55000 },
          { dept: 'HR', avg_salary: 45000 },
        ],
      ],
    ],
  },

  Summary: {
    description: 'Calculate aggregates over an entire relation',
    examples: [
      [
        `Values([
          { value: 10 },
          { value: 20 },
          { value: 30 }
        ])
        |> Summary({
          total: sum("value"),
          count: count(1)
        })`,
        [{ total: 60, count: 3 }],
      ],
    ],
  },

  GenerateSeries: {
    description: 'Generate a series of numbers between two values',
    examples: [
      [`GenerateSeries(1, 3)`, [{ n: 1 }, { n: 2 }, { n: 3 }]],
      [
        `GenerateSeries(0, 4)`,
        [{ n: 0 }, { n: 1 }, { n: 2 }, { n: 3 }, { n: 4 }],
      ],
    ],
  },

  // Table: {
  //   description: 'Define a table contract with schema and attributes',
  //   examples: [
  //     [
  //       `Table(
  //         { schema: "public", name: "users" },
  //         {
  //           id: "int!",
  //           name: "string!",
  //           email: "string"
  //         }
  //       )`,
  //       { schema: "public", name: "users", attributes: { id: "int!", name: "string!", email: "string" } }
  //     ]
  //   ]
  // },

  Derive: {
    description: 'Add new columns to a relation while keeping existing ones',
    examples: [
      [
        `Values([{ x: 10, y: 20 }])
        |> Derive({
          sum: add("x", "y"),
          product: mul("x", "y")
        })`,
        [{ x: 10, y: 20, sum: 30, product: 200 }],
      ],
    ],
  },

  UnionAll: {
    description: 'Combine multiple relations, keeping all rows',
    examples: [
      [
        `UnionAll(
          Values([{ x: 1 }]),
          Values([{ x: 2 }]),
          Values([{ x: 1 }])
        )`,
        [{ x: 1 }, { x: 2 }, { x: 1 }],
      ],
    ],
  },

  // InformationSchema: {
  //   description: 'Query metadata about database schemas and tables',
  //   examples: [
  //     [
  //       `InformationSchema({
  //         schemas: ["public"],
  //         type: "tables"
  //       })`,
  //       [{ schema: "public", name: "users", type: "table" }]
  //     ]
  //   ]
  // },

  // File: {
  //   description: 'Define a file data source with schema',
  //   examples: [
  //     [
  //       `File("data.csv", {
  //         id: "int!",
  //         name: "string!",
  //         score: "float"
  //       })`,
  //       { uri: "data.csv", attributes: { id: "int!", name: "string!", score: "float" } }
  //     ]
  //   ]
  // },

  // LeftJoin: {
  //   description: 'Perform a left join between two relations',
  //   examples: [
  //     [
  //       `LeftJoin(
  //         Values([{ id: 1, name: "Alice" }]),
  //         Values([{ id: 1, age: 30 }]),
  //         (left, right) => ({
  //           on: eq(left.id, right.id),
  //           select: {
  //             id: left.id,
  //             name: left.name,
  //             age: right.age
  //           }
  //         })
  //       )`,
  //       [{ id: 1, name: "Alice", age: 30 }]
  //     ]
  //   ]
  // },

  // RightJoin: {
  //   description: 'Perform a right join between two relations',
  //   examples: [
  //     [
  //       `RightJoin(
  //         Values([{ id: 1, name: "Alice" }]),
  //         Values([{ id: 1, age: 30 }, { id: 2, age: 25 }]),
  //         (left, right) => ({
  //           on: eq(left.id, right.id),
  //           select: {
  //             id: right.id,
  //             name: left.name,
  //             age: right.age
  //           }
  //         })
  //       )`,
  //       [
  //         { id: 1, name: "Alice", age: 30 },
  //         { id: 2, name: null, age: 25 }
  //       ]
  //     ]
  //   ]
  // },

  // InnerJoin: {
  //   description: 'Perform an inner join between two relations',
  //   examples: [
  //     [
  //       `InnerJoin(
  //         Values([{ id: 1, name: "Alice" }]),
  //         Values([{ id: 1, age: 30 }, { id: 2, age: 25 }]),
  //         (left, right) => ({
  //           on: eq(left.id, right.id),
  //           select: {
  //             id: left.id,
  //             name: left.name,
  //             age: right.age
  //           }
  //         })
  //       )`,
  //       [{ id: 1, name: "Alice", age: 30 }]
  //     ]
  //   ]
  // },

  // RelVar: {
  //   description: 'Define a relation variable with optional default value',
  //   examples: [
  //     [
  //       `RelVar(
  //         { name: "active_users", scope: "session" },
  //         { id: "int!", name: "string!" },
  //         { default: Values([{ id: 1, name: "Alice" }]) }
  //       )`,
  //       {
  //         name: "active_users",
  //         scope: "session",
  //         attributes: { id: "int!", name: "string!" },
  //         default: [{ id: 1, name: "Alice" }]
  //       }
  //     ]
  //   ]
  // }
};
